import GlobalLayout from 'layout';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { PageProps } from 'types/global';

import HeroBlog from 'components/Hero/variants/Blog';

import ComponentGenerator from 'utils/builder/ComponentGenerator';
import { RenderBuilderComponents } from 'utils/builder/RenderBuilderContent';
import { fetchLayoutModels, getPageData } from 'utils/builder/builder.sdk';
import { getArticleSchema } from 'utils/schema/articleSchema';

const MODEL = 'press';
const PATH = MODEL;
const FIELDS = [
  'id',
  'data.lastUpdated',
  'data.url',
  'data.blocks',
  'data.title',
  'data.featuredImage',
  'data.summary',
  'data.tags',
  'data.author',
  'data.publishDate',
  'data.readTime',
  'data.breadcrumbTitle',
].join(',');

const Press = ({ pageData, layoutData, preview }: PageProps) => {
  const isInBuilderEditor = !!preview && !!pageData?.data?.isInBuilderEditor;

  // Press releases are news content: emit NewsArticle from the fields the
  // template already loads (title, summary, author, publishDate, image).
  const schemaJson = getArticleSchema(pageData?.data, 'NewsArticle');

  return (
    <>
      {schemaJson && (
        <Head>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJson) }} />
        </Head>
      )}
      <GlobalLayout preview={preview} seoData={pageData.data} {...layoutData}>
        <HeroBlog
          {...pageData.data}
          breadcrumbTitle={
            isInBuilderEditor && !pageData?.data?.breadcrumbTitle
              ? pageData?.data?.title
              : pageData?.data?.breadcrumbTitle
          }
          lastUpdated={pageData?.data?.lastUpdated || ''}
          hideGradient
        />
        <ComponentGenerator>
          <RenderBuilderComponents
            model={MODEL}
            content={pageData}
            data={{ author: JSON.stringify(pageData?.data?.author) }}
          />
        </ComponentGenerator>
      </GlobalLayout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params, query, res, preview: draftPreview }) => {
  const slug = params?.slug as string;
  // BUILDER=1 is set on the production env too, so it cannot distinguish a real
  // visitor from the Builder.io editor / a shared preview link. The editor iframe
  // and Builder preview links carry `builder.space` / `builder.preview`, so gate
  // includeUnpublished on those: real visitors get published-only content (an
  // unpublished slug 404s instead of leaking) while a preview link renders the draft.
  const isBuilderEditor = !!query['builder.space'] || !!query['builder.preview'];
  const pageData = await getPageData(`${PATH}/${slug}`, MODEL, FIELDS, isBuilderEditor);
  const layoutData = await fetchLayoutModels(isBuilderEditor, !!draftPreview);

  if (!pageData?.data && !isBuilderEditor) {
    return { notFound: true };
  }

  // Edge-cache published content like the catch-all page route: browsers never
  // cache, the Vercel edge serves a shared 5-min snapshot and revalidates in the
  // background. Preview requests are never edge-cached so drafts stay private.
  res.setHeader('Cache-Control', 'private, no-cache, must-revalidate');
  // Draft-preview responses (Next.js Preview Mode) carry unpublished chrome,
  // so they must never seed the shared edge cache either.
  if (!isBuilderEditor && !draftPreview) {
    res.setHeader('CDN-Cache-Control', 'public, s-maxage=300, stale-while-revalidate=300');
  }

  return {
    props: { pageData: pageData || {}, layoutData: layoutData || {}, preview: isBuilderEditor },
  };
};

export default Press;
